/**
 * Drops query strings from everything the embed page sends to PostHog and
 * Sentry.
 *
 * The embed page's own address carries the Salesforce record ID and the
 * panel's Upside link (`/salesforce/Opportunity?record=…&link=…`), and its API
 * calls carry them too (`/api/embed/resolve?link=…&record=…`). Both vendors
 * are outside the customer-data trust boundary and attach URLs to events by
 * many routes: the current URL and referrer on every PostHog event, request
 * URLs, breadcrumbs, span descriptions and replay network entries in Sentry,
 * and error messages that quote a request URL. So rather than chase each
 * field, every string in an outgoing payload has the query of any URL in it
 * removed: `…/salesforce/Opportunity?record=006…` becomes
 * `…/salesforce/Opportunity`. The path and host stay, which is what the
 * dashboards use.
 *
 * The dashboard keeps its own allowlist redaction (`lib/posthog-redaction`);
 * this is stricter and applies only on the embed address.
 */

import type { CaptureResult } from "posthog-js";

/**
 * A URL, or a path starting with `/`, followed by a query. The URL part is
 * kept; the `?` and everything up to whitespace, a quote, or a fragment goes.
 */
const URL_QUERY =
  /((?:https?:\/\/[^\s?#"'<>]+)|(?:\/[^\s?#"'<>]*))\?[^\s#"'<>]*/g;

/** *text* with the query of every URL in it removed. */
export function stripQueryFromText(text: string): string {
  return text.includes("?") ? text.replace(URL_QUERY, "$1") : text;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * *copies* maps each object already met to its scrubbed copy, so a repeated
 * reference gets the same scrubbed copy and a cycle ends at the copy being
 * built, never at the original.
 */
function strip(value: unknown, copies: WeakMap<object, unknown>): unknown {
  if (typeof value === "string") return stripQueryFromText(value);
  if (Array.isArray(value)) {
    const known = copies.get(value);
    if (known) return known;
    const copy: unknown[] = [];
    copies.set(value, copy);
    for (const item of value) copy.push(strip(item, copies));
    return copy;
  }
  if (isPlainObject(value)) {
    const known = copies.get(value);
    if (known) return known;
    const copy: Record<string, unknown> = {};
    copies.set(value, copy);
    for (const [key, item] of Object.entries(value)) {
      copy[key] = strip(item, copies);
    }
    return copy;
  }
  return value;
}

/**
 * A copy of *payload* with the query of every URL in every string removed,
 * at any depth (plain objects and arrays). Other values are kept as they are.
 */
export function stripQueryStrings<T>(payload: T): T {
  return strip(payload, new WeakMap()) as T;
}

/** PostHog `before_send` step for the embed page. */
export function stripEmbedEventQueries(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event) return event;
  return stripQueryStrings(event);
}
